/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { jsx } from '@emotion/react';

import * as React from 'react';

import { EnterpriseBadge_UserState as EnterpriseBadgeUserState } from '__generated__/graphql-types';

import user from 'js/lib/user';

import { Typography2 } from '@coursera/cds-core';

import Recommendations from 'bundles/enterprise-learning-path-description-page/components/recommendations/Recommendations';
import CourseItemThumbnailV2 from 'bundles/enterprise-learning-path-description-page/components/syllabus/CourseItemThumbnailV2';
import SkillPathwayBadgeItem from 'bundles/enterprise-learning-path-description-page/components/syllabus/SkillPathwayBadgeItem';
import SkillPathwayBadgeItemV2 from 'bundles/enterprise-learning-path-description-page/components/syllabus/SkillPathwayBadgeItemV2';
import SkillPathwayCourseActions from 'bundles/enterprise-learning-path-description-page/components/syllabus/SkillPathwayCourseActions';
import SkillPathwayCourseActionsV2 from 'bundles/enterprise-learning-path-description-page/components/syllabus/SkillPathwayCourseActionsV2';
import SkillPathwayCourseContent from 'bundles/enterprise-learning-path-description-page/components/syllabus/SkillPathwayCourseContent';
import SkillPathwayCourseContentV2 from 'bundles/enterprise-learning-path-description-page/components/syllabus/SkillPathwayCourseContentV2';
import SkillPathwayCourseItemSubtitle from 'bundles/enterprise-learning-path-description-page/components/syllabus/SkillPathwayCourseItemSubtitle';
import SkillPathwayCourseItemSubtitleV2 from 'bundles/enterprise-learning-path-description-page/components/syllabus/SkillPathwayCourseItemSubtitleV2';
import SkillPathwaySidebarContent from 'bundles/enterprise-learning-path-description-page/components/syllabus/SkillPathwaySidebarContent';
import SkillPathwaySidebarContentV2 from 'bundles/enterprise-learning-path-description-page/components/syllabus/SkillPathwaySidebarContentV2';
import SyllabusItemRedesignV2 from 'bundles/enterprise-learning-path-description-page/components/syllabus/SyllabusItemRedesignV2';
import SyllabusItemTitleRedesignV2 from 'bundles/enterprise-learning-path-description-page/components/syllabus/SyllabusItemTitleRedesignV2';
import SyllabusModuleRedesignV2 from 'bundles/enterprise-learning-path-description-page/components/syllabus/SyllabusModuleRedesignV2';
import { useEnterpriseLearningPathCollectionId } from 'bundles/enterprise-learning-path-description-page/contexts/EnterpriseLearningPathContext';
import { useEnterpriseLearningPathCurriculum } from 'bundles/enterprise-learning-path-description-page/contexts/EnterpriseLearningPathCurriculumContext';
import { useEnterpriseLearningPathCurriculumModuleCount } from 'bundles/enterprise-learning-path-description-page/contexts/EnterpriseLearningPathCurriculumModuleCountContext';
import { useSkillPathway } from 'bundles/enterprise-learning-path-description-page/contexts/SkillPathwayContext';
import { useSkillPathwayPartners } from 'bundles/enterprise-learning-path-description-page/contexts/SkillPathwayPartnersContext';
import type { VspPageV2Data } from 'bundles/enterprise-learning-path-description-page/module-renderer/EnterpriseSkillPathwayProductOverviewModuleRenderer';
import type { EnterpriseLearningPathPageFieldsFragment } from 'bundles/enterprise-learning-path-description-page/queries/__generated__/EnterpriseLearningPathPageFragments';
import { SKILL_PATHWAY_NAV_ITEMS } from 'bundles/enterprise-learning-path-description-page/utils/getPageNavigationLinks';
import {
  getOrderedPartnersFromVspPageData,
  getSkillGoalsFromVspPageData,
  getTimeDurationForVspCourse,
} from 'bundles/enterprise-learning-path-description-page/utils/vspXdpV2Utils';
import EnterpriseBadgeCollectionsProvider from 'bundles/page-config-common/providers/enterprise-skills/EnterpriseBadgeCollectionsProvider';
import { useEnterpriseModalManager } from 'bundles/unified-common/contexts/EnterpriseModalManagerContext';
import { useEnterpriseProductFeatures } from 'bundles/unified-common/contexts/EnterpriseProductFeaturesContext';
import { useProgram, useProgramMembership } from 'bundles/unified-common/contexts/EnterpriseProgramContext';
import { useThirdPartyOrganization } from 'bundles/unified-common/contexts/ThirdPartyOrganizationContext';
import SyllabusItemRedesign from 'bundles/unified-description-page-common/components/modules/syllabus/SyllabusItemRedesign';
import SyllabusItemTitleRedesign from 'bundles/unified-description-page-common/components/modules/syllabus/SyllabusItemTitleRedesign';
import SyllabusModuleRedesign from 'bundles/unified-description-page-common/components/modules/syllabus/SyllabusModuleRedesign';
import { SPECIALIZATION_NAV_ITEMS } from 'bundles/unified-description-page-common/utils/getPageNavigationLinks';
import EnterpriseAllSkillPathwaysDataProvider from 'bundles/unified-home-common/providers/enterprise/EnterpriseAllSkillPathwaysDataProvider';

import _t from 'i18n!nls/enterprise-learning-path-description-page';

type Props = {
  pageData: EnterpriseLearningPathPageFieldsFragment;
  vspPageV2Data?: VspPageV2Data;
};

const EnterpriseSkillPathwaySyllabusModuleRenderer = ({ pageData, vspPageV2Data }: Props) => {
  const pathwayCollectionId = useEnterpriseLearningPathCollectionId();
  const skillPathway = useSkillPathway();
  const modalManager = useEnterpriseModalManager();
  const program = useProgram();
  const productFeatures = useEnterpriseProductFeatures();
  const programMembership = useProgramMembership();
  const thirdPartyOrganization = useThirdPartyOrganization();
  const { curriculumProducts } = useEnterpriseLearningPathCurriculum();
  const { courseModuleCounts } = useEnterpriseLearningPathCurriculumModuleCount();
  const skillPathwayPartners = useSkillPathwayPartners();

  const useV2Variant = user.isAuthenticatedUser();

  const orderedPartners = getOrderedPartnersFromVspPageData(vspPageV2Data);

  const syllabusItems = React.useMemo(() => {
    return (
      skillPathway?.skillAreasWithUserData?.map((skillAreaWithUserData, index) => {
        const course = skillAreaWithUserData.skillArea.course;
        const skillArea = skillAreaWithUserData.skillArea;
        const toggleButtonLabel = _t('Skill goal details');
        const isEnrolledInCourse = curriculumProducts.some(
          (product) =>
            product.productState.__typename === 'ProgramCurriculumProductsV1_programCourseWithStateMember' &&
            product.productState.productState.courseId === course.id &&
            // "0" is a valid value for enrolledAt, so we need to check for undefined and null.
            product.productState.productState.enrolledAt !== undefined &&
            product.productState.productState.enrolledAt !== null
        );

        return (
          <SyllabusItemRedesign
            toggleButtonLabel={toggleButtonLabel}
            key={course.id}
            itemIndex={index + 1}
            thumbnail={
              course.promoPhotoUrl ? (
                <CourseItemThumbnailV2 thumbnailUrl={course.promoPhotoUrl} thumbnailAltText={course.name} />
              ) : undefined
            }
          >
            <SyllabusItemTitleRedesign title={skillArea.name} />
            <SkillPathwayCourseItemSubtitle
              moduleCount={courseModuleCounts[course.id]}
              skillCount={skillArea.skillExpressions.length}
            />
            <React.Fragment>
              <SkillPathwayCourseContent
                skillExpressions={skillArea.skillExpressions.map((skillExpression) => skillExpression.name)}
              />
              {!productFeatures?.isBrowseOnlyProgram && (
                <SkillPathwayCourseActions
                  courseTitle={skillArea.name}
                  courseId={course.id}
                  courseSlug={course.slug}
                  pathwayId={pageData.enterpriseBadge.id}
                  pathwayCollectionId={pathwayCollectionId}
                  programId={program?.id}
                  thirdPartyOrganizationSlug={thirdPartyOrganization?.slug}
                  isProgramMember={!!programMembership?.isProgramMember}
                  isEnrolledInPathway={
                    pageData.userState === EnterpriseBadgeUserState.Enrolled ||
                    pageData.userState === EnterpriseBadgeUserState.Issued
                  }
                  isEnrolledInCourse={isEnrolledInCourse}
                  activateProgramAccessModal={() => {
                    modalManager?.setIsAccessModalDismissed(false);
                  }}
                />
              )}
            </React.Fragment>
          </SyllabusItemRedesign>
        );
      }) || []
    );
  }, [
    pageData,
    pathwayCollectionId,
    program?.id,
    programMembership?.isProgramMember,
    thirdPartyOrganization?.slug,
    curriculumProducts,
    modalManager,
    courseModuleCounts,
    productFeatures?.isBrowseOnlyProgram,
    skillPathway?.skillAreasWithUserData,
  ]);

  const syllabusItemsV2 = React.useMemo(() => {
    return (
      skillPathway?.skillAreasWithUserData
        ?.map((skillAreaWithUserData, index) => {
          const course = skillAreaWithUserData.skillArea.course;
          const skillArea = skillAreaWithUserData.skillArea;

          const courseId = course.id;
          const vspCourseDataV2 = vspPageV2Data?.find((courseItem) => courseItem.id === courseId);
          const timeDuration = getTimeDurationForVspCourse(vspCourseDataV2);

          if (!vspCourseDataV2) {
            return null;
          }

          const isEnrolledInCourse = curriculumProducts.some(
            (product) =>
              product.productState.__typename === 'ProgramCurriculumProductsV1_programCourseWithStateMember' &&
              product.productState.productState.courseId === course.id &&
              // "0" is a valid value for enrolledAt, so we need to check for undefined and null.
              product.productState.productState.enrolledAt !== undefined &&
              product.productState.productState.enrolledAt !== null
          );

          return (
            <SyllabusItemRedesignV2 key={course.id} itemIndex={index + 1} timeDuration={timeDuration}>
              <SyllabusItemTitleRedesignV2 title={skillArea.name} />
              <SkillPathwayCourseItemSubtitleV2
                moduleCount={courseModuleCounts[course.id]}
                skillCount={skillArea.skillExpressions.length}
              />
              <React.Fragment>
                <SkillPathwayCourseContentV2
                  skillExpressions={skillArea.skillExpressions.map((skillExpression) => skillExpression.name)}
                  vspCourseData={vspCourseDataV2}
                  isEnrolledInPathway={
                    pageData.userState === EnterpriseBadgeUserState.Enrolled ||
                    pageData.userState === EnterpriseBadgeUserState.Issued
                  }
                  isEnrolledInCourse={isEnrolledInCourse}
                  isProgramMember={!!programMembership?.isProgramMember}
                  courseId={course.id}
                  courseSlug={course.slug}
                  pathwayId={pageData.enterpriseBadge.id}
                  pathwayCollectionId={pathwayCollectionId}
                  programId={program?.id}
                  programSlug={program?.slug}
                  thirdPartyOrganizationSlug={thirdPartyOrganization?.slug}
                  activateProgramAccessModal={() => {
                    modalManager?.setIsAccessModalDismissed(false);
                  }}
                />
                {!productFeatures?.isBrowseOnlyProgram && (
                  <SkillPathwayCourseActionsV2
                    courseTitle={skillArea.name}
                    courseId={course.id}
                    courseSlug={course.slug}
                    pathwayId={pageData.enterpriseBadge.id}
                    pathwayCollectionId={pathwayCollectionId}
                    programId={program?.id}
                    thirdPartyOrganizationSlug={thirdPartyOrganization?.slug}
                    isProgramMember={!!programMembership?.isProgramMember}
                    isEnrolledInPathway={
                      pageData.userState === EnterpriseBadgeUserState.Enrolled ||
                      pageData.userState === EnterpriseBadgeUserState.Issued
                    }
                    isEnrolledInCourse={isEnrolledInCourse}
                    activateProgramAccessModal={() => {
                      modalManager?.setIsAccessModalDismissed(false);
                    }}
                  />
                )}
              </React.Fragment>
            </SyllabusItemRedesignV2>
          );
        })
        .filter(Boolean) || []
    );
  }, [
    pageData,
    pathwayCollectionId,
    program?.id,
    program?.slug,
    programMembership?.isProgramMember,
    thirdPartyOrganization?.slug,
    curriculumProducts,
    modalManager,
    courseModuleCounts,
    productFeatures?.isBrowseOnlyProgram,
    skillPathway?.skillAreasWithUserData,
    vspPageV2Data,
  ]);

  const allLearningPathwayPartners = React.useMemo(() => {
    return (
      pageData.enterpriseBadge?.badgeActivities?.reduce(
        (uniquePartners, activity) => {
          activity.partners?.forEach((partner) => {
            if (partner.name && partner.logo && !uniquePartners.some((p) => p.name === partner.name)) {
              uniquePartners.push(partner);
            }
          });
          return uniquePartners;
        },
        [] as (typeof pageData.enterpriseBadge.badgeActivities)[0]['partners']
      ) ?? []
    );
  }, [pageData]);

  if (!skillPathway?.skillAreasWithUserData?.length) {
    return null;
  }

  const { totalHours, modulesCount } = getSkillGoalsFromVspPageData(vspPageV2Data);

  return useV2Variant ? (
    <EnterpriseBadgeCollectionsProvider>
      <EnterpriseAllSkillPathwaysDataProvider>
        <SyllabusModuleRedesignV2
          anchorId={SKILL_PATHWAY_NAV_ITEMS.SKILL_GOALS}
          title={_t('Skill goals')}
          totalHours={totalHours}
          modulesCount={modulesCount}
          titleColumnGridOptions={{
            xs: 12,
            md: 9,
          }}
          syllabusContentGridOptions={{
            xs: 12,
            md: 8,
          }}
          sidebarContentGridOptions={{
            xs: 12,
            md: 4,
          }}
          syllabusItems={syllabusItemsV2}
          sidebar={orderedPartners ? <SkillPathwaySidebarContentV2 partners={orderedPartners} /> : null}
          footer={<SkillPathwayBadgeItemV2 />}
        />
        <Recommendations />
      </EnterpriseAllSkillPathwaysDataProvider>
    </EnterpriseBadgeCollectionsProvider>
  ) : (
    <SyllabusModuleRedesign
      anchorId={SPECIALIZATION_NAV_ITEMS.COURSES}
      title={_t('Your path to skill mastery')}
      description={
        <Typography2 component="p">
          {_t('Complete the assessments in each section to verify your mastery in the key skill area.')}
          <div>
            {_t('You can skip content where you already have the knowledge, the assessment will validate your skills.')}
          </div>
        </Typography2>
      }
      titleColumnGridOptions={{
        xs: 12,
        md: 9,
      }}
      syllabusContentGridOptions={{
        xs: 12,
        md: 8,
      }}
      sidebarContentGridOptions={{
        xs: 12,
        md: 4,
      }}
      syllabusItems={syllabusItems}
      sidebar={
        (skillPathwayPartners && skillPathwayPartners.length > 0) || allLearningPathwayPartners.length > 0 ? (
          <SkillPathwaySidebarContent partners={skillPathwayPartners || allLearningPathwayPartners} />
        ) : null
      }
      footer={<SkillPathwayBadgeItem />}
    />
  );
};

export default EnterpriseSkillPathwaySyllabusModuleRenderer;
